import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { HttpClient } from 'src/Helpers/http/http';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import CreateVideoUrl from './CreateVideoUrl';
import UpdateVideoUrl from './UpdateVideoUrl';
import { IVideoConfigure } from './types';
import Toaster from 'src/Toaster/Toaster';

interface IGetApiResponse {
  success: boolean;
  message: string;
  data: IVideoConfigure[];
  total: number;
}

interface ICommonResponse {
  success: boolean;
  message: string;
}

function VideoConfigure() {
  const theme = useTheme();
  const Toast = Toaster();

  const [loading, setLoading] = useState(false);
  const [videoList, setVideoList] = useState<IVideoConfigure[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ▶ Create Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const handleCreateOpen = () => setCreateOpen(true);
  const handleCreateClose = () => setCreateOpen(false);

  // ▶ Delete Modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<IVideoConfigure | null>(
    null
  );

  // ▶ Status updating state
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

  // ▶ Edit Modal state
  const [editOpen, setEditOpen] = useState(false);
  const [videoToUpdate, setVideoToUpdate] = useState<IVideoConfigure | null>(null);

  // Fetch video URLs
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await HttpClient.get<IGetApiResponse>(
          '/api/v1/admin/video-url'
        );

        if (response.success && Array.isArray(response.data)) {
          setVideoList(response.data);
        } else {
          setVideoList([]);
        }
      } catch (error) {
        console.error('Failed to fetch video URL list', error);
        setVideoList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [refreshTrigger]);

  // Open Delete Modal
  const handleOpenDelete = (video: IVideoConfigure) => {
    setSelectedVideo(video);
    setDeleteOpen(true);
  };

  // Close Delete Modal
  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedVideo(null);
  };

  // Delete Video URL
  const handleDelete = async () => {
    if (!selectedVideo?.id) return;
    try {
      setDeleteLoading(true);

      const response = await HttpClient.delete<ICommonResponse>(
        `/api/v1/admin/video-url/${selectedVideo.id}`
      );

      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: response.message || 'Video URL deleted successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        handleCloseDelete();
      } else {
        Toast.fire({
          icon: 'error',
          title: response.message || 'Failed to delete Video URL.',
        });
      }
    } catch (error: any) {
      console.error('Failed to delete video URL', error);
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Error deleting Video URL.',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle Video URL Status
  const handleToggleStatus = async (video: IVideoConfigure) => {
    const nextStatus = video.status === 'active' ? 'inactive' : 'active';
    try {
      setUpdatingStatusId(video.id);

      const response = await HttpClient.patch<ICommonResponse>(
        `/api/v1/admin/video-url/${video.id}`,
        { status: nextStatus }
      );

      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: response.message || `Status updated to ${nextStatus}!`,
        });
        setRefreshTrigger((prev) => prev + 1);
      } else {
        Toast.fire({
          icon: 'error',
          title: response.message || 'Failed to update status.',
        });
      }
    } catch (error: any) {
      console.error('Failed to update status', error);
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Error updating status.',
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Card >
        <CardHeader
          action={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={handleCreateOpen}
                variant='contained'
                sx={{ ml: 2 }}
              >
                Create Video URL
              </Button>
            </Box>
          }
          title='Video Configure List'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SL.</TableCell>
                <TableCell>Video URL</TableCell>
                <TableCell align='center'>Order</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell align='center'>Toggle Status</TableCell>
                <TableCell align='center'>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : videoList?.length ? (
              <TableBody>
                {videoList.map((video: IVideoConfigure, i: number) => (
                  <TableRow hover key={video.id || i}>
                    {/* SL */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {i + 1}
                      </Typography>
                    </TableCell>

                    {/* URL */}
                    <TableCell>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        sx={{
                          wordBreak: 'break-all',
                          maxWidth: '400px',
                        }}
                      >
                        {video?.url}
                      </Typography>
                    </TableCell>

                    {/* Order */}
                    <TableCell align='center'>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        noWrap
                      >
                        {video?.order}
                      </Typography>
                    </TableCell>

                    {/* Status Label */}
                    <TableCell align='center'>
                      <Chip
                        label={video?.status === 'active' ? 'Active' : 'Inactive'}
                        color={video?.status === 'active' ? 'success' : 'error'}
                        size='small'
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>

                    {/* Toggle Status Switch */}
                    <TableCell align='center'>
                      <Switch
                        checked={video?.status === 'active'}
                        onChange={() => handleToggleStatus(video)}
                        disabled={updatingStatusId === video.id}
                        color='primary'
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align='center'>
                      {/* Edit */}
                      <Tooltip title='Edit' arrow>
                        <IconButton
                          onClick={() => {
                            setVideoToUpdate(video);
                            setEditOpen(true);
                          }}
                          sx={{
                            '&:hover': {
                              background: theme.colors.primary.lighter,
                            },
                            color: theme.palette.primary.main,
                            mr: 1,
                          }}
                          color='inherit'
                          size='small'
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      {/* Delete */}
                      <Tooltip title='Delete' arrow>
                        <IconButton
                          onClick={() => handleOpenDelete(video)}
                          sx={{
                            '&:hover': {
                              background: theme.colors.error.lighter,
                            },
                            color: theme.palette.error.main,
                          }}
                          color='inherit'
                          size='small'
                        >
                          <DeleteTwoToneIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Video URL' }}
          maxWidth='sm'
        >
          <CreateVideoUrl
            handleClose={handleCreateClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* EDIT MODAL */}
        <ModalsWrapper
          setShowModal={(val: boolean) => {
            setEditOpen(val);
            if (!val) {
              setVideoToUpdate(null);
            }
          }}
          showModal={editOpen}
          modalData={{ title: 'Update Video URL' }}
          maxWidth='sm'
        >
          {videoToUpdate && (
            <UpdateVideoUrl
              key={videoToUpdate.id}
              video={videoToUpdate}
              handleClose={() => {
                setEditOpen(false);
                setVideoToUpdate(null);
              }}
              setRefreshTrigger={setRefreshTrigger}
            />
          )}
        </ModalsWrapper>

        {/* DELETE CONFIRMATION MODAL */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={handleCloseDelete}
          text={
            selectedVideo
              ? `Want to delete video URL "${selectedVideo.url}"?`
              : 'Want to delete this video URL?'
          }
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />
      </Card>
    </Container>
  );
}

export default VideoConfigure;
